import { StyleSheet, css } from 'aphrodite'
import { useState } from 'react'

export interface SearchBarProps {
  searchValue: string
  onSearchChange: (searchValue: string) => void
  onSearch: () => void
  onClear: () => void
  placeholder?: string
}

const SearchBar: React.FC<SearchBarProps> = ({
  searchValue,
  onSearchChange,
  onSearch,
  onClear,
  placeholder = 'Search across materials, addresses, and more...',
}) => {
  const [isSearchFocused, setIsSearchFocused] = useState(false)

  const hasActiveSearch = searchValue.trim() !== ''

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearch()
    }
  }

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.searchGroup)}>
        <label className={css(styles.label)}>Search</label>
        <div className={css(styles.searchControls)}>
          <div
            className={css(
              styles.searchInputWrapper,
              isSearchFocused && styles.searchInputFocused,
            )}
          >
            <i
              className="fa-solid fa-search"
              style={{ color: '#6B7280', fontSize: '12px' }}
            />
            <input
              type="text"
              className={css(styles.searchInput)}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyPress={handleKeyPress}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              placeholder={placeholder}
            />
          </div>

          <button
            className={css(styles.searchButton)}
            onClick={onSearch}
            title="Search"
          >
            <i className="fa-solid fa-search" />
          </button>

          {hasActiveSearch && (
            <button
              className={css(styles.clearButton)}
              onClick={onClear}
              title="Clear search"
            >
              <i className="fa-solid fa-times" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    flex: 1,
    minWidth: '260px',
    maxWidth: '560px',
    '@media (max-width: 1024px)': {
      width: '100%',
      minWidth: 0,
      maxWidth: 'none',
    },
  },
  searchGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#374151',
  },
  searchControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  searchInputWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    backgroundColor: 'white',
    flex: 1,
    minWidth: 0,
    transition: 'all 0.2s ease',
  },
  searchInputFocused: {
    borderColor: '#1C64F2',
    boxShadow: '0 0 0 2px rgba(28, 100, 242, 0.1)',
  },
  searchInput: {
    border: 'none',
    outline: 'none',
    flex: 1,
    fontSize: '12px',
    color: '#374151',
    backgroundColor: 'transparent',
    '::placeholder': {
      color: '#9CA3AF',
    },
  },
  searchButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    border: '1px solid #1C64F2',
    borderRadius: '6px',
    backgroundColor: '#1C64F2',
    cursor: 'pointer',
    color: 'white',
    ':hover': {
      backgroundColor: '#1955CC',
      borderColor: '#1955CC',
    },
  },
  clearButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    backgroundColor: 'white',
    cursor: 'pointer',
    color: '#6B7280',
    ':hover': {
      borderColor: '#EF4444',
      backgroundColor: '#FEF2F2',
      color: '#EF4444',
    },
  },
})

export default SearchBar
